import React, { useState, useRef } from 'react';
import { FieldLabel, FieldDescription, FieldError } from '../Field';
import { Input } from '../Input';
import { Textarea } from '../Textarea';
import {
  InputGroup,
  InputGroupInput,
  InputGroupAddon,
  InputGroupButton,
} from '../InputGroup';
import { Checkbox } from '../Checkbox';
import { Switch } from '../Switch';
import { RadioGroup, RadioGroupItem } from '../RadioGroup';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../Select';
import { Icon } from '../Icon';
import styles from './FormField.module.css';
import modalStyles from './FormModal.module.css';

/**
 * Renders a single form field based on its `type`. Used internally by
 * FormModal — consumers don't import this directly. Each field type
 * resolves to one input element plus an optional inline error message
 * and help text.
 *
 * The field config object is the SAME shape FormModal exposes; this
 * component just dispatches on `field.type` and binds the value/onChange
 * pair to the appropriate native input.
 *
 * Ported verbatim from Themis (`cybpass-themis-agent/frontend/src/
 * components/ui/FormModal/FormField.jsx`) in Epic 11.X. JSX is
 * byte-for-byte identical; only the CSS layer was retargeted.
 */

/** Legacy label for control types not yet migrated to the 2.0 Field family
 *  (groups / file / display). Migrated fields use the 2.0 <FieldLabel>. */
const GroupLabel = ({ text }) => (
  <label className={modalStyles.formLabel}>
    <span>{text}</span>
  </label>
);

const FormField = ({ field, value, error, onChange, autoFocusRef, allValues, setValue }) => {
  const {
    name,
    type,
    label,
    required,
    placeholder,
    helpText,
    disabled,
    options,
    rows,
    maxLength,
    min,
    max,
    step,
    direction = 'vertical',
  } = field;

  const labelText = required ? `${label} *` : label;

  const renderLabel = (text = labelText) => <GroupLabel text={text} />;

  // ---- Custom escape hatch — consumer-rendered content with access to the
  // live form values (e.g. a derived preview, a select-all link). Non-value-
  // bearing: FormModal skips it for validation / transform / autofocus. ----
  if (type === 'custom') {
    return (
      <div className={modalStyles.formGroup}>
        {field.render?.(allValues, { setValue }) ?? null}
      </div>
    );
  }

  // ---- Read-only display ----
  if (type === 'display') {
    return (
      <div className={modalStyles.formGroup}>
        {renderLabel(label)}
        <div className={styles.displayValue}>{value ?? field.value ?? '—'}</div>
        {helpText && <FieldDescription>{helpText}</FieldDescription>}
      </div>
    );
  }

  // ---- Single checkbox ----
  if (type === 'checkbox') {
    return (
      <div className={modalStyles.formGroup}>
        <div className={styles.checkboxLabel}>
          <Checkbox
            checked={!!value}
            onCheckedChange={(c) => onChange(c)}
            disabled={disabled}
            aria-label={label}
            ref={autoFocusRef}
          />
          <span onClick={() => !disabled && onChange(!value)}>{labelText}</span>
        </div>
        {helpText && <FieldDescription>{helpText}</FieldDescription>}
        {error && <FieldError>{error}</FieldError>}
      </div>
    );
  }

  // ---- Toggle switch ----
  if (type === 'toggle') {
    return (
      <div className={modalStyles.formGroup}>
        <div className={styles.toggleLabel}>
          <span onClick={() => !disabled && onChange(!value)}>{labelText}</span>
          <Switch
            checked={!!value}
            onCheckedChange={(c) => onChange(c)}
            disabled={disabled}
            aria-label={label}
            ref={autoFocusRef}
          />
        </div>
        {helpText && <FieldDescription>{helpText}</FieldDescription>}
        {error && <FieldError>{error}</FieldError>}
      </div>
    );
  }

  // ---- Radio group ----
  if (type === 'radio') {
    return (
      <div className={modalStyles.formGroup}>
        {renderLabel()}
        <RadioGroup
          value={value != null ? String(value) : ''}
          onValueChange={(v) => {
            const opt = options?.find((o) => String(o.value) === v);
            onChange(opt ? opt.value : v);
          }}
          disabled={disabled}
          className={direction === 'horizontal' ? styles.optionGroupHorizontal : undefined}
        >
          {options?.map((opt) => {
            const optId = `${name}-${String(opt.value)}`;
            return (
              <div
                key={String(opt.value)}
                className={`${styles.optionLabel} ${opt.description ? styles.optionLabelTop : ''}`}
              >
                <RadioGroupItem value={String(opt.value)} id={optId} />
                {opt.description ? (
                  // Rich option — label + muted description underneath
                  // (e.g. treatment choices, claim cards). The whole text
                  // block toggles the option.
                  <div
                    className={styles.optionText}
                    onClick={() => !disabled && onChange(opt.value)}
                  >
                    <FieldLabel htmlFor={optId}>{opt.label}</FieldLabel>
                    <FieldDescription>{opt.description}</FieldDescription>
                  </div>
                ) : (
                  <FieldLabel htmlFor={optId} onClick={() => !disabled && onChange(opt.value)}>
                    {opt.label}
                  </FieldLabel>
                )}
              </div>
            );
          })}
        </RadioGroup>
        {helpText && <FieldDescription>{helpText}</FieldDescription>}
        {error && <FieldError>{error}</FieldError>}
      </div>
    );
  }

  // ---- Checkbox group (multi-select via checkboxes) ----
  if (type === 'checkbox-group') {
    const checkedValues = Array.isArray(value) ? value : [];
    const toggleValue = (optValue) => {
      if (checkedValues.includes(optValue)) {
        onChange(checkedValues.filter((v) => v !== optValue));
      } else {
        onChange([...checkedValues, optValue]);
      }
    };
    return (
      <div className={modalStyles.formGroup}>
        {renderLabel()}
        <div
          className={`${styles.optionGroup} ${
            direction === 'horizontal' ? styles.optionGroupHorizontal : ''
          }`}
        >
          {options?.map((opt) => (
            <div key={String(opt.value)} className={styles.optionLabel}>
              <Checkbox
                checked={checkedValues.includes(opt.value)}
                onCheckedChange={() => toggleValue(opt.value)}
                disabled={disabled}
                aria-label={opt.label}
              />
              <span onClick={() => !disabled && toggleValue(opt.value)}>{opt.label}</span>
            </div>
          ))}
        </div>
        {helpText && <FieldDescription>{helpText}</FieldDescription>}
        {error && <FieldError>{error}</FieldError>}
      </div>
    );
  }

  // ---- Single select (2.0 Select) ----
  if (type === 'select') {
    return (
      <div className={modalStyles.formGroup}>
        <FieldLabel htmlFor={name}>{labelText}</FieldLabel>
        <Select
          value={value != null && value !== '' ? String(value) : undefined}
          onValueChange={(v) => {
            const opt = options?.find((o) => String(o.value) === v);
            onChange(opt ? opt.value : v);
          }}
          disabled={disabled}
        >
          <SelectTrigger
            id={name}
            className={styles.selectTrigger}
            aria-invalid={error ? true : undefined}
          >
            <SelectValue placeholder={placeholder ?? 'Select...'} />
          </SelectTrigger>
          <SelectContent>
            {options?.map((opt) => (
              <SelectItem key={String(opt.value)} value={String(opt.value)}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {helpText && <FieldDescription>{helpText}</FieldDescription>}
        {error && <FieldError>{error}</FieldError>}
      </div>
    );
  }

  // ---- Multi-select (scrollable list of checkboxes) ----
  // Native <select multiple> gives a confusing click model — click
  // replaces, Cmd-click toggles — and React's onChange snapshot fires
  // after the browser has already deselected, which causes pre-existing
  // picks to flash. Each row is a checkbox so toggles are independent
  // and the selection state never visually flickers during a click.
  if (type === 'multi-select') {
    const checkedValues = Array.isArray(value) ? value : [];
    const toggleValue = (optValue) => {
      if (checkedValues.includes(optValue)) {
        onChange(checkedValues.filter((v) => v !== optValue));
      } else {
        onChange([...checkedValues, optValue]);
      }
    };
    return (
      <div className={modalStyles.formGroup}>
        {renderLabel()}
        <div
          className={`${styles.multiSelectList} ${error ? styles.error : ''}`}
          role="listbox"
          aria-multiselectable="true"
        >
          {options?.map((opt) => {
            const isChecked = checkedValues.includes(opt.value);
            return (
              <div
                key={String(opt.value)}
                className={`${styles.multiSelectRow} ${isChecked ? styles.multiSelectRowSelected : ''}`}
                role="option"
                aria-selected={isChecked}
              >
                <Checkbox
                  checked={isChecked}
                  onCheckedChange={() => toggleValue(opt.value)}
                  disabled={disabled}
                  aria-label={opt.label}
                />
                <span
                  className={styles.multiSelectLabel}
                  onClick={() => !disabled && toggleValue(opt.value)}
                >
                  {opt.label}
                </span>
              </div>
            );
          })}
        </div>
        {helpText && <FieldDescription>{helpText}</FieldDescription>}
        {error && <FieldError>{error}</FieldError>}
      </div>
    );
  }

  // ---- Textarea ----
  if (type === 'textarea') {
    return (
      <div className={modalStyles.formGroup}>
        <FieldLabel htmlFor={name}>{labelText}</FieldLabel>
        <Textarea
          id={name}
          rows={rows ?? 3}
          maxLength={maxLength}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          ref={autoFocusRef}
        />
        {helpText && <FieldDescription>{helpText}</FieldDescription>}
        {error && <FieldError>{error}</FieldError>}
      </div>
    );
  }

  // ---- List (add items one at a time, displayed as bullet points) ----
  if (type === 'list') {
    return (
      <ListField
        field={field}
        value={value}
        error={error}
        onChange={onChange}
        autoFocusRef={autoFocusRef}
      />
    );
  }

  // ---- File upload (single + multiple) ----
  if (type === 'file' || type === 'files') {
    return (
      <FileField
        field={field}
        value={value}
        error={error}
        onChange={onChange}
        autoFocusRef={autoFocusRef}
        multiple={type === 'files'}
      />
    );
  }

  // ---- Password (with show/hide toggle) ----
  if (type === 'password') {
    return (
      <PasswordField
        field={field}
        value={value}
        error={error}
        onChange={onChange}
        autoFocusRef={autoFocusRef}
      />
    );
  }

  // ---- Date / time / datetime ----
  if (type === 'date' || type === 'time' || type === 'datetime') {
    const inputType = type === 'datetime' ? 'datetime-local' : type;
    return (
      <div className={modalStyles.formGroup}>
        <FieldLabel htmlFor={name}>{labelText}</FieldLabel>
        <Input
          id={name}
          type={inputType}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          min={min}
          max={max}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          ref={autoFocusRef}
        />
        {helpText && <FieldDescription>{helpText}</FieldDescription>}
        {error && <FieldError>{error}</FieldError>}
      </div>
    );
  }

  // ---- Number ----
  if (type === 'number') {
    return (
      <div className={modalStyles.formGroup}>
        <FieldLabel htmlFor={name}>{labelText}</FieldLabel>
        <Input
          id={name}
          type="number"
          value={value ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v === '' ? '' : Number(v));
          }}
          placeholder={placeholder}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          ref={autoFocusRef}
        />
        {helpText && <FieldDescription>{helpText}</FieldDescription>}
        {error && <FieldError>{error}</FieldError>}
      </div>
    );
  }

  // ---- Default: text-like inputs (text, email, url, tel) ----
  const inputType = ['text', 'email', 'url', 'tel'].includes(type) ? type : 'text';
  return (
    <div className={modalStyles.formGroup}>
      <FieldLabel htmlFor={name}>{labelText}</FieldLabel>
      <Input
        id={name}
        type={inputType}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        ref={autoFocusRef}
      />
      {helpText && <FieldDescription>{helpText}</FieldDescription>}
      {error && <FieldError>{error}</FieldError>}
    </div>
  );
};

/* ---------- Sub-components for complex field types ---------- */

const PasswordField = ({ field, value, error, onChange, autoFocusRef }) => {
  const [visible, setVisible] = useState(false);
  const labelText = field.required ? `${field.label} *` : field.label;
  return (
    <div className={modalStyles.formGroup}>
      <FieldLabel htmlFor={field.name}>{labelText}</FieldLabel>
      <InputGroup data-invalid={error ? '' : undefined}>
        <InputGroupInput
          id={field.name}
          type={visible ? 'text' : 'password'}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          maxLength={field.maxLength}
          disabled={field.disabled}
          aria-invalid={error ? true : undefined}
          ref={autoFocusRef}
        />
        <InputGroupAddon align="inline-end">
          <InputGroupButton
            variant="ghost"
            size="icon-xs"
            onClick={() => setVisible((v) => !v)}
            tabIndex={-1}
            aria-label={visible ? 'Hide password' : 'Show password'}
          >
            <Icon name={visible ? 'eye-off' : 'eye'} />
          </InputGroupButton>
        </InputGroupAddon>
      </InputGroup>
      {field.helpText && <FieldDescription>{field.helpText}</FieldDescription>}
      {error && <FieldError>{error}</FieldError>}
    </div>
  );
};

const ListField = ({ field, value, error, onChange, autoFocusRef }) => {
  const { name, label, required, placeholder, helpText, disabled } = field;
  const labelText = required ? `${label} *` : label;
  const items = Array.isArray(value) ? value : [];
  const inputRef = useRef(null);

  const addItem = () => {
    const val = inputRef.current?.value?.trim();
    if (val) {
      onChange([...items, val]);
      inputRef.current.value = '';
      inputRef.current.focus();
    }
  };

  return (
    <div className={modalStyles.formGroup}>
      <FieldLabel htmlFor={name}>{labelText}</FieldLabel>
      {items.length > 0 && (
        <ul className={styles.listItems}>
          {items.map((item, idx) => (
            <li key={idx} className={styles.listItem}>
              <span>{item}</span>
              <button
                type="button"
                className={styles.listItemRemove}
                onClick={() => onChange(items.filter((_, i) => i !== idx))}
                aria-label="Remove"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <InputGroup>
        <InputGroupInput
          id={name}
          ref={(el) => {
            inputRef.current = el;
            if (autoFocusRef) autoFocusRef.current = el;
          }}
          placeholder={placeholder}
          disabled={disabled}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addItem();
            }
          }}
        />
        <InputGroupAddon align="inline-end">
          <InputGroupButton
            variant="ghost"
            size="icon-xs"
            aria-label="Add item"
            title="Add (or press Enter)"
            onClick={addItem}
          >
            <Icon name="add" />
          </InputGroupButton>
        </InputGroupAddon>
      </InputGroup>
      {helpText && <FieldDescription>{helpText}</FieldDescription>}
      {error && <FieldError>{error}</FieldError>}
    </div>
  );
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileField = ({ field, value, error, onChange, autoFocusRef, multiple }) => {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);
  const labelText = field.required ? `${field.label} *` : field.label;
  const files = multiple ? (Array.isArray(value) ? value : []) : value ? [value] : [];

  const handleFiles = (fileList) => {
    const incoming = Array.from(fileList);
    if (multiple) {
      const next = [...files, ...incoming];
      const capped = field.maxFiles ? next.slice(0, field.maxFiles) : next;
      onChange(capped);
    } else {
      onChange(incoming[0] ?? null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  const removeFile = (idx) => {
    if (multiple) {
      onChange(files.filter((_, i) => i !== idx));
    } else {
      onChange(null);
    }
  };

  return (
    <div className={modalStyles.formGroup}>
      <FieldLabel>{labelText}</FieldLabel>
      <div
        className={`${styles.dropZone} ${isDragging ? styles.dropZoneActive : ''} ${
          error ? styles.dropZoneInvalid : ''
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={(el) => {
            inputRef.current = el;
            if (autoFocusRef) autoFocusRef.current = el;
          }}
          type="file"
          accept={field.accept}
          multiple={multiple}
          onChange={(e) => e.target.files && handleFiles(e.target.files)}
          className={styles.fileInputHidden}
          disabled={field.disabled}
        />
        <div className={styles.dropZoneIcon} aria-hidden="true">
          <Icon name="upload" size={24} />
        </div>
        <div className={styles.dropZoneTitle}>
          {isDragging
            ? 'Drop files here'
            : multiple
            ? 'Drop files here or click to browse'
            : 'Drop a file here or click to browse'}
        </div>
        {field.helpText && (
          <div className={styles.dropZoneHint}>{field.helpText}</div>
        )}
      </div>
      {files.length > 0 && (
        <ul className={styles.fileList}>
          {files.map((file, idx) => (
            <li key={`${file.name}-${idx}`} className={styles.fileCard}>
              <div className={styles.fileCardIcon} aria-hidden="true">
                <Icon name="document" size={20} />
              </div>
              <div className={styles.fileCardInfo}>
                <div className={styles.fileCardName} title={file.name}>
                  {file.name}
                </div>
                <div className={styles.fileCardMeta}>
                  <span className={styles.fileCardType}>{fileTypeLabel(file)}</span>
                  <span className={styles.fileCardSep}>·</span>
                  <span>{formatFileSize(file.size)}</span>
                </div>
              </div>
              <button
                type="button"
                className={styles.fileCardRemoveBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(idx);
                }}
                aria-label={`Remove ${file.name}`}
              >
                <Icon name="close" size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && <FieldError>{error}</FieldError>}
    </div>
  );
};

const fileTypeLabel = (file) => {
  const ext = file.name.split('.').pop()?.toUpperCase() ?? 'FILE';
  return ext.length > 6 ? 'FILE' : ext;
};

export default FormField;
